import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { RootState } from '../store'
import { SyncStatus } from '../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/models'

interface DocumentSummaryResult {
  summary: string
}

interface DocumentSummaryState {
  isLoading: boolean
  pollingEnabled: boolean
  result: DocumentSummaryResult | null
  documentIsNotRelatedToClient: boolean
  syncStatus: SyncStatus[]
  executeIsLoading: boolean
  interactionIsLoading: boolean
  aiAgentId: string | null
  isEditing: boolean
}

const initialState: DocumentSummaryState = {
  isLoading: true,
  pollingEnabled: false,
  result: null,
  documentIsNotRelatedToClient: false,
  syncStatus: [],
  interactionIsLoading: false,
  executeIsLoading: false,
  aiAgentId: null,
  isEditing: false,
}

export const documentSummarySlice = createSlice({
  name: 'documentSummary',
  initialState,
  reducers: {
    setDocumentSummaryLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    startDocumentSummaryPolling: (state) => {
      state.pollingEnabled = true
    },
    stopDocumentSummaryPolling: (state) => {
      state.pollingEnabled = false
    },
    setDocumentSummaryResult: (state, action: PayloadAction<DocumentSummaryResult>) => {
      state.result = { ...state.result, ...action.payload }
    },
    clearDocumentSummaryResult: (state) => {
      state.result = null
    },
    setDocumentIsNotRelatedToClient: (state, action: PayloadAction<boolean>) => {
      state.documentIsNotRelatedToClient = action.payload
    },
    setDocumentSummarySyncStatus: (state, action: PayloadAction<SyncStatus[]>) => {
      state.syncStatus = action.payload
    },
    setDocumentSummaryExecutionLoading: (state, action: PayloadAction<boolean>) => {
      state.executeIsLoading = action.payload
    },
    setDocumentSummaryInteractionLoading: (state, action: PayloadAction<boolean>) => {
      state.interactionIsLoading = action.payload
    },
    setDocumentSummaryAiAgentId: (state, action: PayloadAction<string>) => {
      state.aiAgentId = action.payload
    },
    setDocumentSummaryEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload
    },
    resetDocumentSummary: () => initialState,
  },
})

export const {
  setDocumentSummaryLoading,
  startDocumentSummaryPolling,
  stopDocumentSummaryPolling,
  setDocumentSummaryResult,
  clearDocumentSummaryResult,
  setDocumentIsNotRelatedToClient,
  setDocumentSummarySyncStatus,
  setDocumentSummaryExecutionLoading,
  setDocumentSummaryInteractionLoading,
  setDocumentSummaryAiAgentId,
  setDocumentSummaryEditing,
  resetDocumentSummary,
} = documentSummarySlice.actions

export const selectDocumentSummaryLoading = (state: RootState) => state.processDocument.documentSummary.isLoading

export const selectDocumentSummaryPollingEnabled = (state: RootState) => state.processDocument.documentSummary.pollingEnabled

export const selectDocumentSummaryResult = (state: RootState) => state.processDocument.documentSummary.result

export const selectDocumentIsNotRelatedToClient = (state: RootState) => state.processDocument.documentSummary.documentIsNotRelatedToClient

export const selectDocumentSummaryExecutionLoading = (state: RootState) => state.processDocument.documentSummary.executeIsLoading

export const selectDocumentSummaryInteractionLoading = (state: RootState) => state.processDocument.documentSummary.interactionIsLoading

export const selectDocumentSummaryAiAgentId = (state: RootState) => state.processDocument.documentSummary.aiAgentId

export const selectDocumentSummaryEditing = (state: RootState) => state.processDocument.documentSummary.isEditing

export const selectDocumentSummarySyncStatus = (state: RootState) => state.processDocument.documentSummary.syncStatus
